/**
 * 外部轨迹时间线组件（总部包裹详情 → 外部轨迹 Tab）。
 *
 * ## 时间展示策略
 *
 * 后端 `order_tracking_info.event_list` 中的 `localTime` 已是事件发生地的本地时间，
 * 官网轨迹页直接展示 `${localTime} (${localGmtOffset})`，不做浏览器时区转换。
 *
 * 本文件对外暴露两套格式化函数，供不同场景使用：
 *
 * | 函数 | 使用场景 | 行为 |
 * |------|----------|------|
 * | `formatEventTime` | 外部轨迹 Tab（本组件） | 原样展示 `localTime`，GMT 偏移由 `formatGmtLabel` 单独一行展示 |
 * | `formatCompareEventTime` | 轨迹对比（`CustomParcelsTimeline`） | 结合 `localGmtOffset` 解析为 UTC 后，转为浏览器本地时区，格式 `YYYY/MM/DD HH:mm:ss` |
 *
 * 轨迹对比需与内部轨迹（浏览器本地时区）对齐阅读，故保留时区转换；外部轨迹 Tab 与官网一致，不做转换。
 */
import React from 'react';
import { Timeline } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { ExternalOrderEventSummaryType } from '../graphql/types/OrderDetailType';

interface ExternalParcelsTimelineProps {
    items: ExternalOrderEventSummaryType[];
}

type OfficialLikeExternalEvent = ExternalOrderEventSummaryType & {
    iso3166Cc?: string | null;
    iso3166Sc?: string | null;
    cityUppercase?: string | null;
    postalCode?: string | null;
    podImageCount?: number | null;
    isOnlySortingTracking?: boolean | null;
};

const safeTrim = (value: string | null | undefined): string => (value ? value.trim() : '');

const formatLocation = (item: OfficialLikeExternalEvent, t: (key: string, fallback?: string) => string): string => {
    const location = safeTrim(item.location);
    const city = safeTrim(item.cityUppercase);
    const state = safeTrim(item.iso3166Sc);
    const postalCode = safeTrim(item.postalCode);
    const countryCode = safeTrim(item.iso3166Cc);
    const parts = [location, city, state, postalCode].filter(Boolean);
    if (countryCode) {
        parts.push(`[${countryCode}]`);
    }
    return parts.length > 0 ? parts.join(', ') : t('central.parcels.timeline.locationUnknown', 'Unknown');
};

const getStatusColorClass = (status: string): string => {
    const normalizedStatus = status.toLowerCase();
    if (normalizedStatus.includes('delivery failed') || normalizedStatus.includes('pickup failed')) {
        return 'bg-red-100 text-red-800';
    }
    if (normalizedStatus.includes('delivered')) {
        return 'bg-green-100 text-green-800';
    }
    if (normalizedStatus.includes('out of delivery')) {
        return 'bg-blue-100 text-blue-800';
    }
    if (normalizedStatus.includes('pickup')) {
        return 'bg-yellow-100 text-yellow-800';
    }
    if (normalizedStatus.includes('create')) {
        return 'bg-gray-100 text-gray-800';
    }
    return 'bg-gray-100 text-gray-800';
};

/** 获取展示用 location：SYSTEM 等转为友好文案 */
export const getDisplayLocation = (
    location: string | null | undefined,
    t: (key: string, fallback?: string) => string
): string | null => {
    const loc = safeTrim(location);
    if (!loc) return null;
    const upper = loc.toUpperCase();
    if (upper === 'SYSTEM') return t('central.parcels.timeline.locationSystem', '系统');
    return loc;
};

const formatGmtLabel = (rawOffset: string | null | undefined): string | null => {
    const offset = safeTrim(rawOffset);
    if (!offset) return null;
    if (offset.toUpperCase().startsWith('GMT')) {
        return `(${offset})`;
    }
    if (offset.startsWith('+') || offset.startsWith('-')) {
        return `(GMT${offset})`;
    }
    return `(GMT ${offset})`;
};

const normalizeGmtOffset = (rawOffset: string | null | undefined): string | null => {
    if (!rawOffset) return null;
    const s = rawOffset.trim().toUpperCase();
    if (!s) return null;
    const cleaned = s.replace(/^UTC|^GMT/, '');
    const match = cleaned.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);
    if (!match) return null;
    const sign = match[1];
    const hour = match[2].padStart(2, '0');
    const minute = (match[3] ?? '00').padStart(2, '0');
    return `${sign}${hour}:${minute}`;
};

const parseExternalEventDate = (
    timeStr: string | null | undefined,
    localGmtOffset?: string | null
): Date | null => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const normalized = timeStr.trim();
    if (!normalized) return null;

    const hasTimezoneInfo = /([zZ]|[+-]\d{2}:?\d{2})$/.test(normalized);
    if (hasTimezoneInfo) {
        const date = new Date(normalized);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const normalizedOffset = normalizeGmtOffset(localGmtOffset);
    if (normalizedOffset) {
        const date = new Date(normalized.replace(' ', 'T') + normalizedOffset);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const fallback = new Date(normalized);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * 外部轨迹 Tab 时间展示：直接返回后端 `localTime`，不做时区转换。
 * @param timeStr 后端 `localTime`，如 `2023-10-15 14:30:00`
 * @param _localGmtOffset 保留参数以兼容调用方，本函数不使用（GMT 由 `formatGmtLabel` 单独展示）
 * @example formatEventTime('2023-10-15 14:30:00') → `'2023-10-15 14:30:00'`
 */
export const formatEventTime = (
    timeStr: string | null | undefined,
    _localGmtOffset?: string | null
): string => {
    if (!timeStr || typeof timeStr !== 'string') return '-';
    const normalized = timeStr.trim();
    return normalized || '-';
};

/**
 * 轨迹对比时间展示：解析 `localTime` + `localGmtOffset` 后转为浏览器本地时区。
 * @param timeStr 后端 `localTime`
 * @param localGmtOffset 事件当地 GMT 偏移，如 `GMT-8:00` 或 `-8:00`
 * @example formatCompareEventTime('2023-10-15 14:30:00', 'GMT-8:00') → 浏览器本地时间，如 `'2023/10/16 06:30:00'`（UTC+8 环境）
 */
export const formatCompareEventTime = (
    timeStr: string | null | undefined,
    localGmtOffset?: string | null
): string => {
    const parsedDate = parseExternalEventDate(timeStr, localGmtOffset);
    if (!parsedDate) return '-';
    const parsed = dayjs(parsedDate);
    return parsed.isValid() ? parsed.format('YYYY/MM/DD HH:mm:ss') : (timeStr ?? '-');
};

/** 外部轨迹节点主文案：直接展示后端 description，空值显示 '-' */
export const getDisplayDescription = (description: string | null | undefined): string => {
    const normalizedDescription = (description || '').trim();
    return normalizedDescription || '-';
};

/**
 * 按时间从新到旧排序；localTime 相同时保持接口返回的原始顺序
 */
const sortExternalEventsNewestFirst = (list: ExternalOrderEventSummaryType[]): ExternalOrderEventSummaryType[] => {
    return list
        .map((item, idx) => ({ item, idx }))
        .sort((a, b) => {
            const ta = (a.item.eventTime || '').trim();
            const tb = (b.item.eventTime || '').trim();
            if (ta !== tb) return tb.localeCompare(ta);
            return a.idx - b.idx;
        })
        .map(({ item }) => item);
};

/**
 * 外部轨迹时间线（展示结构与官网轨迹节点保持一致）
 * 仅在总部包裹详情页使用
 */
const ExternalParcelsTimeline: React.FC<ExternalParcelsTimelineProps> = ({ items }) => {
    const { t } = useTranslation();

    if (!items || items.length === 0) {
        return (
            <div className="text-gray-500 text-sm py-4">
                {t('central.parcels.timeline.noExternalTrack', '暂无承运商轨迹')}
            </div>
        );
    }

    const timelineItems = sortExternalEventsNewestFirst(items).map((rawItem, index) => {
        const item = rawItem as OfficialLikeExternalEvent;
        const statusText = getDisplayDescription(item.description);
        const locationText = formatLocation(item, t);
        const timeText = formatEventTime(item.eventTime, item.localGmtOffset);
        const gmtLabel = formatGmtLabel(item.localGmtOffset);
        const detailText = statusText.includes('\n') ? statusText.split('\n').slice(1).join(' ') : '';
        const mainStatusText = statusText.includes('\n') ? statusText.split('\n')[0] : statusText;

        return {
            key: `${index}-${item.eventTime || index}`,
            color: index === 0 ? 'blue' : 'gray',
            children: (
                <div className="space-y-2">
                    <div className="flex justify-between">
                        <span className={`text-base mr-6 flex-1 min-w-0 ${index === 0 ? 'font-medium' : ''}`}>
                            {mainStatusText}
                        </span>
                        <div className="text-right shrink-0">
                            <div className="text-sm whitespace-nowrap">{timeText}</div>
                            {gmtLabel && <div className="text-xs text-gray-500 whitespace-nowrap">{gmtLabel}</div>}
                        </div>
                    </div>
                    <div className="text-sm text-gray-700">{locationText}</div>
                    {detailText && (
                        <div className="text-sm text-gray-600">
                            {detailText}
                        </div>
                    )}
                    {item.operatorAccountName && item.operatorAccountName.trim().length > 0 && (
                        <div className="text-gray-500 text-sm">
                            {t('components.timeline.operatorName')}: {item.operatorAccountName.trim()}
                        </div>
                    )}
                    {item.podImageCount != null && item.podImageCount > 0 && (
                        <div className="text-xs text-gray-500">
                            POD: {item.podImageCount}
                        </div>
                    )}
                </div>
            )
        };
    });

    return <Timeline items={timelineItems} />;
};

export default ExternalParcelsTimeline;
